/* ============================================================
   ClearWyn Facility Services Ltd, contact.js
   Quote form wizard: 4-step navigation, inline validation,
   auto-advance, live summary, progressive disclosure, and
   callback form submit gating.
   ============================================================ */

'use strict';

/* -------------------------------------------------------
   0. Shared: Turnstile (Cloudflare CAPTCHA) submit gating
   The widget only injects <input name="cf-turnstile-response"> once it has
   verified. Submitting before that token exists makes send-quote.php /
   send-callback.php reject the post and bounce the visitor back to a blank
   form, which reads as the form "resetting" and loses the lead. This holds
   the submit and fires it automatically the instant the token lands, so a
   slow-to-verify or never-clicked captcha can't cost us a lead.
   ------------------------------------------------------- */
function wireTurnstileGate(opts) {
  const form   = opts.form;
  const submit = opts.submitBtn || null;
  const msgEl  = opts.msgEl || null;
  const state  = { pending: false, timer: null };

  function token() {
    const i = form.querySelector('[name="cf-turnstile-response"]');
    return (i && i.value) ? i.value : '';
  }
  function show(text, isError) {
    if (!msgEl) return;
    msgEl.textContent = text;
    msgEl.style.color = isError ? '#b42318' : 'var(--color-text-muted)';
    msgEl.hidden = false;
  }
  function hide() {
    if (!msgEl) return;
    msgEl.hidden = true;
    msgEl.textContent = '';
  }

  // Exposed globally so the widget's data-callback attributes can reach them.
  window[opts.successName] = function () {
    if (state.timer) { clearTimeout(state.timer); state.timer = null; }
    hide();
    if (state.pending) {
      state.pending = false;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submit || undefined);
      else form.submit();
    }
  };
  window[opts.errorName] = function () {
    if (state.timer) { clearTimeout(state.timer); state.timer = null; }
    if (state.pending) {
      state.pending = false;
      show("We couldn't load the human-verification check. Please refresh the page and try again.", true);
    }
  };

  return {
    // Call from the form's own submit handler. Returns true when the post may
    // proceed; false means it has been held (the caller must stop).
    ensure: function (e) {
      if (token()) { hide(); return true; }
      e.preventDefault();
      state.pending = true;
      show("Just a moment, we're confirming you're human. Your request will send automatically.");
      if (state.timer) clearTimeout(state.timer);
      state.timer = setTimeout(function () {
        if (state.pending) show("Verification is taking longer than usual. Please refresh the page and try again.", true);
      }, 12000);
      return false;
    }
  };
}

/* -------------------------------------------------------
   1. Show error message from URL query param
   Successful submissions now redirect to dedicated thank-you
   pages (/quote-thank-you.html, /callback-thank-you.html), so
   only the error path is handled inline here.
   ------------------------------------------------------- */
(function initErrorMessage() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('error') !== '1') return;

  const errorBox = document.getElementById('form-error-message');
  const reason   = (params.get('reason') || '').replace(/[^a-z0-9_]/gi, '').slice(0, 60);
  if (errorBox) {
    if (reason) {
      const codeNote = document.createElement('div');
      codeNote.style.cssText = 'font-size: 0.75rem; opacity: 0.75; margin-top: 0.5rem;';
      codeNote.textContent = 'Reference code: ' + reason;
      errorBox.appendChild(codeNote);
    }
    errorBox.style.display = 'block';
    errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  if (window.history && window.history.replaceState) {
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    url.searchParams.delete('reason');
    window.history.replaceState(null, '', url.toString());
  }
})();

/* -------------------------------------------------------
   2. Callback form: enable submit only when name + phone + email valid
   ------------------------------------------------------- */
(function initCallbackForm() {
  const form = document.getElementById('callback-form');
  if (!form) return;
  const btn = form.querySelector('button[type="submit"]');
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function valid() {
    const name  = (form.elements['contact_name']?.value || '').trim();
    const phone = (form.elements['phone']?.value        || '').trim();
    const email = (form.elements['email']?.value        || '').trim();
    const digits = phone.replace(/\D/g, '');
    return name.length > 0 && digits.length >= 10 && EMAIL_RE.test(email);
  }

  function update() { if (btn) btn.disabled = !valid(); }
  form.addEventListener('input', update);
  form.addEventListener('change', update);
  update();

  // Hold submission until Turnstile has verified, so an early click never
  // bounces the visitor back to an empty callback form.
  const gate = wireTurnstileGate({
    form: form,
    submitBtn: btn,
    msgEl: document.getElementById('callback-captcha-msg'),
    successName: 'onCallbackCaptcha',
    errorName: 'onCallbackCaptchaError',
  });
  form.addEventListener('submit', function (e) {
    if (!valid()) { e.preventDefault(); return; }
    gate.ensure(e);
  });
})();

/* -------------------------------------------------------
   3. Stepper +/- buttons for room count inputs
   ------------------------------------------------------- */
(function initSteppers() {
  document.querySelectorAll('.stepper-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const targetId = btn.dataset.stepperTarget;
      const input    = document.getElementById(targetId);
      if (!input) return;
      const action = btn.dataset.stepperAction;
      const min    = parseInt(input.min || '0', 10);
      const max    = parseInt(input.max || '100', 10);
      let val      = parseInt(input.value || '0', 10);
      if (isNaN(val)) val = 0;
      if (action === 'increment') val = Math.min(max, val + 1);
      if (action === 'decrement') val = Math.max(min, val - 1);
      input.value = val;
      input.dispatchEvent(new Event('input',  { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
})();

/* -------------------------------------------------------
   4. "Prefer to chat" shortcut scrolls to callback card
   ------------------------------------------------------- */
(function initChatShortcut() {
  const link = document.getElementById('chat-shortcut-link');
  const card = document.getElementById('callback-card');
  if (!link || !card) return;
  link.addEventListener('click', function (e) {
    e.preventDefault();
    card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    card.classList.remove('is-highlighted');
    void card.offsetWidth;
    card.classList.add('is-highlighted');
    setTimeout(function () {
      const firstInput = document.getElementById('cb_contact_name');
      if (firstInput) firstInput.focus({ preventScroll: true });
    }, 550);
  });
})();

/* -------------------------------------------------------
   5. Quote form wizard: step navigation + validation + summary
   ------------------------------------------------------- */
(function initQuoteWizard() {
  const form = document.getElementById('quote-form');
  if (!form) return;

  const stepEls       = Array.from(form.querySelectorAll('.wizard-step'));
  const pillEls       = Array.from(form.querySelectorAll('.wizard-step-pill'));
  const progressBar   = form.querySelector('.wizard-progress');
  const progressFill  = document.getElementById('wizard-progress-fill');
  const backBtn       = form.querySelector('[data-wizard-action="back"]');
  const continueBtn   = form.querySelector('[data-wizard-action="continue"]');
  const submitBtn     = form.querySelector('[data-wizard-action="submit"]');
  const alertBox      = document.getElementById('wizard-alert');
  const alertText     = document.getElementById('wizard-alert-text');

  // Cloudflare Turnstile gate (see wireTurnstileGate). Holds the final submit
  // until the captcha token exists so a half-verified check never bounces the
  // visitor back to a blank form.
  const captchaGate = wireTurnstileGate({
    form: form,
    submitBtn: submitBtn,
    msgEl: document.getElementById('quote-captcha-msg'),
    successName: 'onQuoteCaptcha',
    errorName: 'onQuoteCaptchaError',
  });

  const summaryList   = document.getElementById('wss-list');
  const summaryEmpty  = document.getElementById('wss-empty');
  const mobileSummary = document.getElementById('wizard-summary-mobile');
  const mobileChips   = document.getElementById('wsm-chips');

  let currentStep     = 1;
  let autoAdvanceTmr  = null;
  // Auto-advance is disabled. A misclick on a card shouldn't yank the user to
  // the next step before they've reviewed their answer or filled in optional
  // siblings (e.g. Rooms after Frequency on step 2). Users advance explicitly
  // via the Continue button.
  const AUTO_ADVANCE_NAMES = new Set();
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* ---------- helpers ---------- */
  function getChecked(name) {
    return form.querySelector('input[name="' + name + '"]:checked');
  }
  function val(name) {
    const el = form.elements[name];
    if (!el) return '';
    if (typeof el.value === 'string') return el.value.trim();
    return '';
  }
  function getMode() {
    const fac = getChecked('facility_type');
    return (fac && fac.value === 'postcon') ? 'postcon' : 'normal';
  }
  function getStepOrder() {
    return getMode() === 'postcon' ? [1, 2, 4] : [1, 2, 3, 4];
  }
  function isLastStep(step) {
    const o = getStepOrder();
    return o[o.length - 1] === step;
  }
  function nextStepOf(step) {
    const o = getStepOrder();
    const i = o.indexOf(step);
    return (i >= 0 && i < o.length - 1) ? o[i + 1] : null;
  }
  function prevStepOf(step) {
    const o = getStepOrder();
    const i = o.indexOf(step);
    return (i > 0) ? o[i - 1] : null;
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }

  /* ---------- progressive disclosure of conditional sub-fields ---------- */
  function setVisible(selector, show) {
    const el = form.querySelector(selector) || document.querySelector(selector);
    if (!el) return;
    el.style.display = show ? '' : 'none';
    el.querySelectorAll('input, select, textarea').forEach(function (inp) {
      if (show) {
        inp.disabled = false;
        if (inp.dataset._wasRequired === '1') {
          inp.required = true;
          delete inp.dataset._wasRequired;
        }
      } else {
        if (inp.required) {
          inp.dataset._wasRequired = '1';
          inp.required = false;
        }
        inp.disabled = true;
      }
    });
  }

  function updateProgressiveDisclosure() {
    const facility       = getChecked('facility_type');
    const isPC           = !!facility && facility.value === 'postcon';
    const spaceSize      = getChecked('space_size');
    const freq           = getChecked('frequency');
    const timing         = getChecked('timing');
    const facilityLocked = getChecked('facility_locked');
    // Access method is "select all that apply" — a NodeList of checked boxes.
    const accessChecked  = form.querySelectorAll('input[name="access_method[]"]:checked');

    // Step 1 clean_type hidden in postcon mode
    setVisible('#clean-type-section', !isPC);

    // Step 2 space size: bucket radios hidden in postcon, only custom sqft visible
    setVisible('#space-size-buckets', !isPC);
    if (isPC) {
      setVisible('#custom-sqft-group', true);
      const customSqft = document.getElementById('custom_sqft');
      if (customSqft) customSqft.required = true;
      const star = document.getElementById('custom-sqft-required-star');
      if (star) star.style.display = '';
      form.querySelectorAll('input[name="space_size"]:checked').forEach(function (r) { r.checked = false; });
    } else {
      const customSqft2 = document.getElementById('custom_sqft');
      if (customSqft2) customSqft2.required = false;
      const star = document.getElementById('custom-sqft-required-star');
      if (star) star.style.display = 'none';
      setVisible('#custom-sqft-group', !!spaceSize && spaceSize.value === 'custom');
    }

    // Step 2 frequency + rooms hidden in postcon
    setVisible('#frequency-field-wrap', !isPC);
    setVisible('#rooms-field-wrap',     !isPC);

    // Step 3 nested timing details
    setVisible('#after-hours-details', !isPC && !!timing && timing.value === 'after_hours');
    setVisible('#daytime-details',     !isPC && !!timing && timing.value === 'daytime');
    setVisible('#weekend-details',     !isPC && !!timing && timing.value === 'weekend');
    setVisible('#access-method-group', !isPC && !!facilityLocked && facilityLocked.value === 'yes');
    setVisible('#onsite-contact-group', !isPC && Array.prototype.some.call(accessChecked, function (el) { return el.value === 'on_site_contact'; }));

    // Step 3 contract-length: hidden in postcon or if one-time frequency
    setVisible('#contract-length-field-wrap', !isPC && (!freq || freq.value !== 'one_time'));
  }

  /* ---------- refresh CSS state on labels + text-input validity ---------- */
  function refreshCheckedLabels() {
    form.querySelectorAll('.quote-radio, .quote-checkbox, .wizard-card, .wizard-pill').forEach(function (label) {
      const input = label.querySelector('input');
      if (!input) return;
      const checked = !!input.checked;
      label.classList.toggle('is-checked', checked);
      // Belt-and-suspenders: apply the selected-state inline so we're not at the
      // mercy of stylesheet cascade quirks. Unset on uncheck so hover still reads.
      const isCard = label.classList.contains('wizard-card');
      const isPill = label.classList.contains('wizard-pill');
      if (checked) {
        if (isCard) {
          label.style.setProperty('border-color', 'var(--color-cta)', 'important');
          label.style.setProperty('background-color', 'rgba(242, 125, 0, 0.14)', 'important');
          label.style.setProperty('background-image', 'none', 'important');
          label.style.setProperty('box-shadow', '0 0 0 3px rgba(242, 125, 0, 0.18)', 'important');
          const icon = label.querySelector('.wizard-card-icon');
          if (icon) icon.style.setProperty('color', 'var(--color-cta-light)', 'important');
        } else if (isPill) {
          label.style.setProperty('border-color', 'var(--color-cta)', 'important');
          label.style.setProperty('background-color', 'rgba(242, 125, 0, 0.14)', 'important');
          label.style.setProperty('background-image', 'none', 'important');
          label.style.setProperty('color', '#FFFFFF', 'important');
          label.style.setProperty('box-shadow', '0 0 0 2px rgba(242, 125, 0, 0.18)', 'important');
          const bullet = label.querySelector('.wizard-pill-bullet');
          if (bullet) {
            bullet.style.setProperty('border-color', 'var(--color-cta)', 'important');
            bullet.style.setProperty('background-color', 'var(--color-cta)', 'important');
          }
        }
      } else {
        if (isCard) {
          label.style.removeProperty('border-color');
          label.style.removeProperty('background-color');
          label.style.removeProperty('background-image');
          label.style.removeProperty('box-shadow');
          const icon = label.querySelector('.wizard-card-icon');
          if (icon) icon.style.removeProperty('color');
        } else if (isPill) {
          label.style.removeProperty('border-color');
          label.style.removeProperty('background-color');
          label.style.removeProperty('background-image');
          label.style.removeProperty('color');
          label.style.removeProperty('box-shadow');
          const bullet = label.querySelector('.wizard-pill-bullet');
          if (bullet) {
            bullet.style.removeProperty('border-color');
            bullet.style.removeProperty('background-color');
          }
        }
      }
    });
  }

  function refreshFieldValidity() {
    // Wizard-field wrappers with data-field-key: show green check when "filled"
    form.querySelectorAll('.wizard-field[data-field-key]').forEach(function (wrap) {
      const key = wrap.dataset.fieldKey;
      const filled = isFieldFilled(key);
      wrap.classList.toggle('is-valid', filled);
      if (filled) {
        wrap.classList.remove('is-invalid');
        const err = form.querySelector('[data-error-for="' + key + '"]');
        if (err) { err.textContent = ''; err.style.display = 'none'; }
      }
    });

    // Text input fields on step 4: individual validity
    ['contact_name', 'business_name', 'address', 'city', 'phone', 'email'].forEach(function (id) {
      const input = form.elements[id];
      if (!input) return;
      const errEl = form.querySelector('[data-error-for="' + id + '"]');
      const v = (input.value || '').trim();
      let ok = v.length > 0;
      if (id === 'phone') ok = v.replace(/\D/g, '').length >= 10;
      if (id === 'email') ok = EMAIL_RE.test(v);
      input.classList.toggle('input-valid',   ok);
      input.classList.toggle('input-invalid', v.length > 0 && !ok);
      if (ok && errEl) { errEl.textContent = ''; errEl.style.display = 'none'; }
    });
  }

  function isFieldFilled(key) {
    switch (key) {
      case 'facility_type': return !!getChecked('facility_type');
      case 'clean_type':    return !!getChecked('clean_type');
      case 'space_size': {
        if (getMode() === 'postcon') {
          const sq = parseInt(val('custom_sqft') || '0', 10);
          return !!sq && sq > 0;
        }
        const sp = getChecked('space_size');
        if (!sp) return false;
        if (sp.value === 'custom') {
          const sq = parseInt(val('custom_sqft') || '0', 10);
          return !!sq && sq > 0;
        }
        return true;
      }
      case 'frequency': return !!getChecked('frequency');
      case 'timing':    return !!getChecked('timing');
      default: return false;
    }
  }

  /* ---------- step navigation ---------- */
  function showStep(step, scroll) {
    if (scroll === undefined) scroll = true;
    currentStep = step;

    stepEls.forEach(function (s) {
      const stepNum  = parseInt(s.dataset.step, 10);
      const isActive = stepNum === step;
      s.hidden = !isActive;
      s.classList.toggle('is-active', isActive);
    });

    const order = getStepOrder();
    const idx   = order.indexOf(step);

    pillEls.forEach(function (p) {
      const pillStep = parseInt(p.dataset.stepPill, 10);
      const pillIdx  = order.indexOf(pillStep);
      p.classList.remove('is-active', 'is-done');
      p.hidden = pillIdx === -1;
      if (pillIdx >= 0 && pillIdx < idx) p.classList.add('is-done');
      if (pillIdx === idx) p.classList.add('is-active');
    });

    const pct = Math.round(((idx + 1) / order.length) * 100);
    if (progressFill) progressFill.style.width = pct + '%';
    if (progressBar)  progressBar.setAttribute('aria-valuenow', String(pct));

    if (backBtn)     backBtn.hidden     = idx === 0;
    const last = isLastStep(step);
    if (continueBtn) continueBtn.hidden = last;
    if (submitBtn)   submitBtn.hidden   = !last;

    hideAlert();

    const active = form.querySelector('.wizard-step.is-active');
    if (active) {
      const heading = active.querySelector('.wizard-step-title');
      if (heading) {
        setTimeout(function () {
          try { heading.focus({ preventScroll: true }); } catch (e) {}
        }, 30);
      }
    }

    // Scroll the top of the form card back into view so the user sees the new step.
    // Skipped on initial page load so the success / error scroll target is not overridden.
    const card = form.closest('.form-card');
    if (card && scroll) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function goNext() {
    clearTimeout(autoAdvanceTmr);
    const errs = validateStep(currentStep);
    if (errs.length) { showErrors(errs); return; }
    const next = nextStepOf(currentStep);
    if (next) showStep(next);
  }

  function goBack() {
    clearTimeout(autoAdvanceTmr);
    const prev = prevStepOf(currentStep);
    if (prev) showStep(prev);
  }

  /* ---------- per-step validation ---------- */
  function validateStep(step) {
    const errs = [];
    const mode = getMode();

    if (step === 1) {
      if (!getChecked('facility_type')) {
        errs.push({ field: 'facility_type', msg: 'Pick the kind of space you run.' });
      }
      if (mode === 'normal' && !getChecked('clean_type')) {
        errs.push({ field: 'clean_type', msg: 'Pick the kind of clean you need.' });
      }
    }

    if (step === 2) {
      if (mode === 'postcon') {
        const sq = parseInt(val('custom_sqft') || '0', 10);
        if (!sq || sq <= 0) {
          errs.push({ field: 'custom_sqft', msg: 'Enter the square footage of the space.' });
        }
      } else {
        const sp = getChecked('space_size');
        if (!sp) {
          errs.push({ field: 'space_size', msg: 'Pick a size range.' });
        } else if (sp.value === 'custom') {
          const sq = parseInt(val('custom_sqft') || '0', 10);
          if (!sq || sq <= 0) {
            errs.push({ field: 'custom_sqft', msg: 'Enter your square footage.' });
          }
        }
        if (!getChecked('frequency')) {
          errs.push({ field: 'frequency', msg: 'Pick how often you want cleaning.' });
        }
      }
    }

    if (step === 3 && mode === 'normal') {
      if (!getChecked('timing')) {
        errs.push({ field: 'timing', msg: 'Pick when we should clean.' });
      }
    }

    if (step === 4) {
      if (!val('contact_name'))  errs.push({ field: 'contact_name',  msg: 'Enter your name.' });
      if (!val('business_name')) errs.push({ field: 'business_name', msg: 'Enter your business name.' });
      if (!val('address'))       errs.push({ field: 'address',       msg: 'Enter the service address.' });
      if (!val('city'))          errs.push({ field: 'city',          msg: 'Enter the city.' });
      const phone = val('phone').replace(/\D/g, '');
      if (!phone)               errs.push({ field: 'phone', msg: 'Enter your phone number.' });
      else if (phone.length < 10) errs.push({ field: 'phone', msg: 'Phone needs at least 10 digits.' });
      const email = val('email');
      if (!email)               errs.push({ field: 'email', msg: 'Enter your email.' });
      else if (!EMAIL_RE.test(email)) errs.push({ field: 'email', msg: 'Enter a valid email address.' });
    }

    return errs;
  }

  function showErrors(errs) {
    form.querySelectorAll('.wizard-field.is-invalid, .form-group.is-invalid')
        .forEach(function (f) { f.classList.remove('is-invalid'); });

    let firstErrEl = null;

    errs.forEach(function (err) {
      const wrap = form.querySelector('[data-field-key="' + err.field + '"]');
      const errBox = form.querySelector('[data-error-for="' + err.field + '"]');
      // form.elements[name] may return a RadioNodeList when there are multiple radios with that name.
      // Guard against that by explicitly resolving a single Element (prefer getElementById, then a named input).
      let input = document.getElementById(err.field);
      if (!input) {
        const byName = form.querySelector('input[name="' + err.field + '"], textarea[name="' + err.field + '"], select[name="' + err.field + '"]');
        input = byName || null;
      }
      const group = (input && typeof input.closest === 'function') ? input.closest('.form-group') : null;

      if (errBox) {
        errBox.textContent = err.msg;
        errBox.style.display = 'block';
      }
      if (wrap) {
        wrap.classList.add('is-invalid');
        if (!firstErrEl) firstErrEl = wrap;
      } else if (group) {
        group.classList.add('is-invalid');
        if (input) input.classList.add('input-invalid');
        if (!firstErrEl) firstErrEl = group;
      }
    });

    if (alertBox) {
      alertBox.hidden = false;
      if (alertText) {
        alertText.textContent = errs.length === 1
          ? errs[0].msg
          : 'Please complete the highlighted fields before continuing.';
      }
    }
    if (firstErrEl) firstErrEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function hideAlert() {
    if (alertBox) alertBox.hidden = true;
  }

  /* ---------- auto-advance ---------- */
  function scheduleAutoAdvance(changedName) {
    if (!AUTO_ADVANCE_NAMES.has(changedName)) return;
    clearTimeout(autoAdvanceTmr);
    autoAdvanceTmr = setTimeout(function () {
      const active = form.querySelector('.wizard-step.is-active');
      if (!active) return;

      // If the current step is now valid, advance to the next step (unless we're on the last step).
      const errs = validateStep(currentStep);
      if (errs.length === 0 && !isLastStep(currentStep)) {
        goNext();
        return;
      }

      // Otherwise scroll to and focus the next unfilled required field within the current step.
      const firstErr = errs[0];
      if (!firstErr) return;
      const wrap = active.querySelector('[data-field-key="' + firstErr.field + '"]');
      const input = active.querySelector('#' + firstErr.field);
      const target = wrap || (input && input.closest('.form-group'));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = target.querySelector('input:not([type=hidden]):not(:disabled), select, textarea');
        if (focusable) setTimeout(function () { focusable.focus({ preventScroll: true }); }, 200);
      }
    }, 300);
  }

  /* ---------- live summary (desktop sidebar + mobile chips) ---------- */
  function labelForRadio(name) {
    const el = getChecked(name);
    if (!el) return null;
    const label = el.closest('label');
    if (!label) return el.value;
    // Prefer the explicit text node (.wizard-card-label or .wizard-pill-label) so we don't
    // accidentally pull in descriptor / hint / bullet-marker text.
    const explicit = label.querySelector('.wizard-card-label, .wizard-pill-label');
    if (explicit) return explicit.textContent.trim();
    return label.textContent.replace(/\s+/g, ' ').trim();
  }

  function updateSummary() {
    const items = [];
    const isPC  = getMode() === 'postcon';

    const fac   = labelForRadio('facility_type');
    const clean = labelForRadio('clean_type');
    if (fac) {
      let line = fac;
      if (clean && !isPC) line += ' · ' + clean;
      items.push({ k: 'What', v: line });
    }

    const sp = getChecked('space_size');
    if (isPC) {
      const customSqft = val('custom_sqft');
      if (customSqft) items.push({ k: 'Size', v: '~' + Number(customSqft).toLocaleString() + ' sq ft' });
    } else if (sp) {
      let size = labelForRadio('space_size');
      if (sp.value === 'custom') {
        const customSqft = val('custom_sqft');
        if (customSqft) size = '~' + Number(customSqft).toLocaleString() + ' sq ft';
      }
      const freqLabel = labelForRadio('frequency');
      if (freqLabel) size += ' · ' + freqLabel;
      items.push({ k: 'How much', v: size });
    }

    if (!isPC) {
      const rooms = [];
      const b = parseInt(val('bathrooms') || '0', 10);
      const k = parseInt(val('kitchens') || '0', 10);
      const m = parseInt(val('meeting_rooms') || '0', 10);
      if (b) rooms.push(b + ' bath');
      if (k) rooms.push(k + ' kitchen');
      if (m) rooms.push(m + ' meeting');
      if (rooms.length) items.push({ k: 'Rooms', v: rooms.join(' · ') });

      const tim = labelForRadio('timing');
      if (tim) items.push({ k: 'When', v: tim });

      const addons = Array.from(form.querySelectorAll('input[name="addons[]"]:checked'));
      if (addons.length) {
        // Show the count in the compact mobile chip / sidebar line. First-name callout is
        // nice to have but "2 extras" keeps the summary visually tight when the list grows.
        items.push({ k: 'Add-ons', v: addons.length + (addons.length === 1 ? ' extra' : ' extras') });
      }

      const cl = labelForRadio('contract_length');
      if (cl) items.push({ k: 'Contract', v: cl });
    }

    // Desktop sidebar
    if (summaryList) {
      summaryList.innerHTML = items.map(function (it) {
        return '<li><span class="wss-k">' + escapeHtml(it.k) + '</span>' +
                   '<span class="wss-v">' + escapeHtml(it.v) + '</span></li>';
      }).join('');
    }
    if (summaryEmpty) summaryEmpty.style.display = items.length ? 'none' : '';

    // Mobile sticky chips
    if (mobileChips) {
      mobileChips.innerHTML = items.length
        ? items.map(function (it) {
            return '<span class="wsm-chip">' + escapeHtml(it.v) + '</span>';
          }).join('')
        : '<span class="wsm-chip wsm-chip--empty">We\'ll fill this in as you answer</span>';
    }
    if (mobileSummary) mobileSummary.classList.toggle('is-empty', items.length === 0);
  }

  /* ---------- event wiring ---------- */
  form.addEventListener('change', function (e) {
    refreshCheckedLabels();
    updateProgressiveDisclosure();
    refreshFieldValidity();
    updateSummary();

    const t = e.target;
    if (!t) return;
    if (t.type === 'radio' && AUTO_ADVANCE_NAMES.has(t.name)) {
      scheduleAutoAdvance(t.name);
    }
    // Changing facility_type in postcon mode may change step order. If the user is currently
    // on a step that no longer exists in the order, snap back to step 2 (safe home in both modes).
    if (t.name === 'facility_type') {
      const order = getStepOrder();
      if (order.indexOf(currentStep) === -1) showStep(2);
      else {
        // Keep the pills in sync after a mode change
        showStep(currentStep);
      }
    }
  });

  form.addEventListener('input', function (e) {
    refreshCheckedLabels();
    refreshFieldValidity();
    updateSummary();

    // Clear the stale error shown on a text input once the user starts editing it
    const t = e.target;
    if (t && t.classList && t.classList.contains('input-invalid')) {
      const errEl = form.querySelector('[data-error-for="' + t.id + '"]');
      if (errEl) { errEl.textContent = ''; errEl.style.display = 'none'; }
    }
    hideAlert();
  });

  if (continueBtn) {
    continueBtn.addEventListener('click', function (e) {
      e.preventDefault();
      goNext();
    });
  }
  if (backBtn) {
    backBtn.addEventListener('click', function (e) {
      e.preventDefault();
      goBack();
    });
  }

  // Pressing Enter inside a text input should continue (not submit prematurely) on steps 1 to 3.
  form.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    const t = e.target;
    if (t && t.tagName === 'TEXTAREA') return;
    if (isLastStep(currentStep)) return; // let step 4 submit via the submit button
    // Don't intercept Enter inside a button
    if (t && (t.tagName === 'BUTTON')) return;
    e.preventDefault();
    goNext();
  });

  // Submit gating: re-validate the current step (step 4) and ensure prior steps were filled in.
  form.addEventListener('submit', function (e) {
    const stepErrs = validateStep(currentStep);
    if (stepErrs.length) {
      e.preventDefault();
      showErrors(stepErrs);
      return;
    }
    const order = getStepOrder();
    for (let i = 0; i < order.length; i++) {
      const s = order[i];
      if (s === currentStep) continue;
      const earlier = validateStep(s);
      if (earlier.length) {
        e.preventDefault();
        showStep(s);
        showErrors(earlier);
        return;
      }
    }
    // Everything validated. Final gate: hold for the Turnstile token so the
    // post is never rejected and bounced back to an empty form. ensure()
    // preventDefaults and auto-submits once the token lands.
    if (!captchaGate.ensure(e)) return;
    // All good, let native submit proceed to send-quote.php
  });

  /* ---------- init ---------- */
  updateProgressiveDisclosure();
  refreshCheckedLabels();
  refreshFieldValidity();
  updateSummary();
  showStep(1, false);
})();
